import * as turf from '@turf/turf';
import { featureCollection } from '@turf/turf';
import { getSecondaryHeadlandFeature } from './layerAugmenters';
import { DEFAULT_CUSTOM_LAYER_COLOR } from '../constants/colors';
import { CUSTOM_LAYER_TYPE } from '../constants/layers';
import { generateDbId, MODEL_PREFIXES } from './utils';
import { AREA_UNITS, DISTANCE_UNITS } from '../constants/units';

// Don't use this character in layer ids.
const LAYER_FEATURE_ID_SEPARATOR = ':';
const FEATUREID_PREFIX = `${MODEL_PREFIXES.LAYER_FEATURE}_`;

// Generate an id to use for features in the geojson, in the form `{Layer.id}:{LayerFeature.id}`.
// NOTE: the `featureId` arg is *not* a LayerFeature.id - it's currently unused.
export const generateGeojsonFeatureId = (layerId, featureId = null) => {
  const featureIdSegment = featureId
    ? FEATUREID_PREFIX + featureId
    : generateDbId('LAYER_FEATURE');

  return [layerId, LAYER_FEATURE_ID_SEPARATOR, featureIdSegment].join('');
};

const isValidLayerId = (id, layerName) =>
  id?.includes(layerName + LAYER_FEATURE_ID_SEPARATOR + FEATUREID_PREFIX);

/**
 * Get geojson feature id from Layer.id and LayerFeature.id
 */
export const geojsonFeatureIdFromRecordIds = (layerId, layerFeatureRecordId) =>
  `${layerId}${LAYER_FEATURE_ID_SEPARATOR}${layerFeatureRecordId}`;

/**
 * Get Layer.id from geojson feature id.
 */
export const layerIdFromGeojsonFeatureId = (geojsonFeatureId) => {
  const [layerId] = geojsonFeatureId.split(LAYER_FEATURE_ID_SEPARATOR);
  return layerId;
};

/**
 * Get LayerFeature.id from geojson feature id.
 */
export const layerFeatureIdFromGeojsonFeatureId = (geojsonFeatureId) => {
  const [, layerFeatureId] = geojsonFeatureId.split(LAYER_FEATURE_ID_SEPARATOR);
  return layerFeatureId;
};

// Add or replace the layer id in the feature.
export const addOrReplaceLayerId = (feature, layerName) => {
  const layerFeatureId = generateGeojsonFeatureId(layerName);
  return {
    ...feature,
    id: layerFeatureId,
    properties: {
      ...feature.properties,
      id: layerFeatureId,
    },
  };
};

export const fillLayerIds = (layerData, layerName) => {
  let addedIds = false;
  if (!layerData?.features)
    return { dataWithIds: featureCollection([]), addedIds };
  const dataWithIds = {
    ...layerData,
    features: layerData.features.map((feature) => {
      if (isValidLayerId(feature.properties?.id, layerName)) return feature;
      addedIds = true;
      return addOrReplaceLayerId(feature, layerName);
    }),
  };
  return { dataWithIds, addedIds };
};

export const getArea = ({ data }) => {
  if (!data?.features?.length) return null;
  try {
    // turf.union accepts a FeatureCollection with at least two geometries
    return turf.area(data.features.length === 1 ? data : turf.union(data));
  } catch (err) {
    // If an invalid geojson was imported and the area cannot be calculated, we
    // return 0 to indicate that the area calculation failed.
    console.error(err);
    return 0;
  }
};

/**
 * The first version of Custom Layers created LayerFeature records
 * with IDs that differed from the geojson features IDs. We've since
 * changed the way we generate geojson feature IDs so they match
 * LayerFeature.id, but dev environments may have old data that
 * was created before this change.
 *
 * This function removes any features from the geojson that don't have a
 * corresponding LayerFeature record, but only in non-prod environments.
 */
export const cleanUpFeaturesInDev = (layerData, layerFeatureRecords) => {
  let modified = false;

  // Don't run this in prod - this is just to fix some issues with dev/staging data.
  if (window.location.hostname === 'app.overyield.com') {
    return { cleanedUpData: layerData, modified };
  }

  const cleanedUpData = {
    ...layerData,
    features: layerData.features
      // Remove features without a corresponding LayerFeature record:
      .map((feature) => {
        const geojsonFeatureId = feature.properties?.id;
        const layerFeatureId = layerFeatureIdFromGeojsonFeatureId(
          geojsonFeatureId,
        );
        if (layerFeatureId in layerFeatureRecords) return feature;

        modified = true;
        return null;
      })
      .filter((f) => f),
  };

  return { cleanedUpData, modified };
};

export const getHeadlandArea = ({ data, width }) => {
  if (!data?.features?.length) return null;
  const headlandDonuts = data.features
    .map((feature) => {
      const secondaryFeature = getSecondaryHeadlandFeature(feature, width);
      const orderedFeatures = feature.properties.isInnerFeature
        ? [feature, secondaryFeature]
        : [secondaryFeature, feature];

      // A breaking change was introduced to the turf library in v6.5.0 by https://github.com/Turfjs/turf/pull/2130
      // The change made it so that `turf.mask` now mutates its first argument. By cloning the features here, we avoid this.
      // TODO: once https://github.com/Turfjs/turf/pull/2635 is merged we can upgrade turf and remove this.
      const [innerFeature, outerFeature] = orderedFeatures.map((f) =>
        turf.clone(f),
      );
      return secondaryFeature ? turf.mask(innerFeature, outerFeature) : feature;
    })
    .filter((feature) => feature);
  if (headlandDonuts.length === 0) return null;
  return getArea({ data: turf.featureCollection(headlandDonuts) });
};

// Creates a shallow copy with empty properties.
export const copyFeatureWithoutProperties = (feature) => {
  if (!feature) return null;
  // Remove id and properties.
  const { type, geometry } = feature;
  return {
    type,
    geometry,
    properties: {},
  };
};

export const layerIsCustom = (layer) => layer?.layerType === CUSTOM_LAYER_TYPE;
export const getLayerId = (layer) =>
  layerIsCustom(layer) ? layer.id : layer.layerType;

export const getCustomLayerColor = (layer) =>
  layer?.color ?? DEFAULT_CUSTOM_LAYER_COLOR;

export const layerHasData = (layer) =>
  layerIsCustom(layer) || !!layer?.data?.features.length;

/**
 * Produces parameters that can be used with UpsertLayer to identify this layer based on its `layerId`.
 *
 * For custom layers, `layerId` is the actual ID, and for other layers, it's the layer type.
 *
 * NOTE: `farmId` must also be added as a parameter for UpsertLayer.
 */
export const getLayerIdParams = (layerId) =>
  layerId.startsWith(`${MODEL_PREFIXES.LAYER}_`)
    ? { id: layerId, layerType: CUSTOM_LAYER_TYPE }
    : { layerType: layerId };

export const filterFeaturesByType = (obj, type) => {
  const newObj = { ...obj };
  newObj.features = (newObj.features ?? []).filter(
    ({ geometry }) => geometry.type === type,
  );
  return newObj;
};

// Non-custom layers specify a `units` key, but custom layers need separate units for different feature types.
export const defaultLayerUnits = { polygon: 'acres', lineString: 'feet' };
export const defaultLayerUnitSpec = {
  polygon: AREA_UNITS,
  lineString: DISTANCE_UNITS,
};

export const getLayerArea = (layer, polygons) => {
  try {
    const { units, getArea: getAreaFn = getArea } = layer;
    const targetUnit = units?.turf ?? defaultLayerUnits.polygon;

    if (polygons.features.length === 0) return null;

    return turf.convertArea(
      getAreaFn({ ...layer, data: polygons }),
      'meters',
      targetUnit,
    );
  } catch (err) {
    console.error(err);
    return null;
  }
};

export const getLayerLength = (layer, lines) => {
  try {
    const { units } = layer;
    const targetUnit = units?.turf ?? defaultLayerUnits.lineString;

    if (lines.features.length === 0) return null;

    return turf.length(lines, { units: targetUnit });
  } catch (err) {
    console.error(err);
    return null;
  }
};

/* Returns a list of layers sorted for display */
export const sortLayers = (layers) =>
  // Put custom layers at the top and sort them by name
  [...layers].sort((a, b) => {
    const aIsCustom = layerIsCustom(a);
    const bIsCustom = layerIsCustom(b);

    if (!aIsCustom && bIsCustom) return 1;
    if (aIsCustom && !bIsCustom) return -1;

    if (aIsCustom && bIsCustom) {
      return a.displayName.localeCompare(b.displayName);
    }

    return 0; // (don't adjust the sort order of non-custom layers)
  });
